// components/RewardDetailsModal.tsx
import React from 'react';

type SequenceStep = { stepIndex: number; value: number; sequenceID: string };
type Transaction = { id: string; amount: number; type: string; transactionType: string; createdAt: string };

type Reward = {
  id: string;
  orderId?: string;
  reason?: string;
  amount: number;
  rewardCount: number;
  rewardReset: number;
  isRewardActive: boolean;
  createdAt: string;
  rewardSeq: number;
  parentRewardSeq?: number | null;
  sequenceSteps: SequenceStep[];
  transactions: Transaction[];
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  rewards: Reward[];
  loading: boolean;
};

const RewardDetailsModal: React.FC<Props> = ({ isOpen, onClose, rewards, loading }) => {
  if (!isOpen) return null;

  // Group rewards by parent-child relationship
  const groupedRewards = rewards.reduce<Record<number, Reward[]>>((acc, reward) => {
    const parentSeq = reward.parentRewardSeq ?? reward.rewardSeq;
    if (!acc[parentSeq]) acc[parentSeq] = [];
    acc[parentSeq].push(reward);
    return acc;
  }, {});

  const renderReward = (reward: Reward, isChild = false) => (
    <div key={reward.id} style={{ marginBottom: 16, marginLeft: isChild ? 24 : 0 }}>
      <h3 style={{ marginBottom: 4 }}>
        {isChild ? 'Child Reward' : 'Parent Reward'} — Seq: {reward.rewardSeq}
      </h3>
      <p>
        <strong>Order:</strong> {reward.orderId || '-'} | <strong>Amount:</strong> {reward.amount} |{' '}
        <strong>Count:</strong> {reward.rewardCount} | <strong>Resets:</strong> {reward.rewardReset}
      </p>
      <p>
        <strong>Active:</strong> {reward.isRewardActive ? 'Yes' : 'No'} |{' '}
        <strong>Created:</strong> {new Date(reward.createdAt).toLocaleString()}
      </p>

      {reward.transactions.length > 0 && (
        <>
          <h4>Transactions</h4>
          <ul>
            {reward.transactions.map((t) => (
              <li key={t.id}>
                {t.transactionType} - {t.type}: ₹{t.amount} on{' '}
                {new Date(t.createdAt).toLocaleString()}
              </li>
            ))}
          </ul>
        </>
      )}

      {reward.sequenceSteps.length > 0 && (
        <>
          <h4>Sequence Steps</h4>
          <ul>
            {reward.sequenceSteps.map((s, idx) => (
              <li key={idx}>
                Step {s.stepIndex}: {s.value} (Seq ID: {s.sequenceID})
              </li>
            ))}
          </ul>
        </>
      )}

      <hr />
    </div>
  );

  return (
    <div
      style={{
        position: 'fixed',
        top: 0, left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          padding: '25px',
          borderRadius: 8,
          width: '90%',
          maxWidth: 800,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <h2 style={{ marginBottom: 16 }}>Reward Details</h2>

        {loading ? (
          <p>Loading...</p>
        ) : Object.keys(groupedRewards).length === 0 ? (
          <p>No rewards found for this sequence.</p>
        ) : (
          Object.values(groupedRewards).map((group) => {
            const parent = group.find((r) => !r.parentRewardSeq);
            const children = group.filter((r) => r.parentRewardSeq);

            return (
              <div key={parent?.rewardSeq || Math.random()}>
                {parent && renderReward(parent, false)}
                {children.map((child) => renderReward(child, true))}
              </div>
            );
          })
        )}

        <div style={{ textAlign: 'right' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: '#FF5722',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              marginTop: 16,
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RewardDetailsModal;
